import * as React from 'react';
import {
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Typography,
  Box,
} from '@mui/material';
import { useAppContext } from '../../contexts/AppContext';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useNavigate } from 'react-router';
import CartButtons from '../CartButtons/CartButtons';
import { buildProductPath } from '../../utils/seo';

export default function ProductCard(props) {
  const { products, title, layout = 'grid' } = props;
  const navigate = useNavigate();

  const { favoriteItems, addToFavorite, removeFromFavorite } = useAppContext();

  const favoriteDetails = (product) => {
    const isFavorite = favoriteItems.some((fItem) => fItem.id === product.id);
    return (
      <FavoriteIcon
        color='pink'
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: isFavorite ? 'red' : '#9e9e9e',
          fontSize: '1.3rem',
          zIndex: 1,
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          isFavorite ? removeFromFavorite(product.id) : addToFavorite(product);
        }}
      />
    );
  };

  const renderVariantSummary = (product) => {
    const variants = Array.isArray(product.variants)
      ? product.variants.filter(Boolean)
      : [];

    if (variants.length === 0 && !product.size) {
      return null;
    }

    const labels = variants.length > 0 ? variants : [product.size];

    return (
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{
          fontSize: { xs: '0.82rem', sm: '0.86rem' },
          mt: 0.35,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: '2.4em',
        }}
      >
        {labels.join(' / ')}
      </Typography>
    );
  };

  return (
    <>
      <Typography
        variant='subtitle1'
        noWrap
        sx={{ fontWeight: '700', fontSize: '1.15rem', mb: 1, mx: 2, mt: 2.5 }}
      >
        {title}
      </Typography>
      <Box
        className={`product-grid${
          layout === 'scroll' ? ' product-grid--scroll' : ''
        }`}
      >
        {products?.map((prod) => (
          <Card
            key={prod.id}
            className={`product-card${
              layout === 'scroll' ? ' product-card--scroll' : ''
            }`}
            sx={{
              width: '100%',
              maxWidth: '100%',
              borderRadius: 6,
              boxShadow: '0 14px 26px rgba(17, 24, 39, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
            onClick={() => navigate(buildProductPath(prod))}
          >
            {favoriteDetails(prod)}
            <CardMedia
              component='img'
              alt={prod.name}
              height='132'
              image={prod.image}
              sx={{
                objectFit: 'contain',
                p: { xs: 1.25, sm: 1.5 },
                pt: { xs: 1.5, sm: 2 },
                width: '100%',
                maxWidth: { xs: 132, sm: 148 },
                alignSelf: 'center',
              }}
            />
            <CardContent sx={{ px: { xs: 1.5, sm: 1.75 }, py: 0, pb: 0.5 }}>
              <Typography
                variant='subtitle1'
                sx={{
                  fontWeight: '700',
                  fontSize: { xs: '0.94rem', sm: '0.98rem' },
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  minHeight: '2.6em',
                }}
              >
                {prod.name}
              </Typography>
              {prod.description ? (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{
                    fontSize: '0.82rem',
                    mt: 0.15,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {prod.description}
                </Typography>
              ) : null}
              {renderVariantSummary(prod)}
            </CardContent>
            <CardActions
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                px: { xs: 1.5, sm: 1.75 },
                pt: 0.75,
                pb: 1.2,
                gap: 1,
              }}
            >
              <Typography
                variant='subtitle1'
                sx={{
                  fontWeight: '800',
                  fontSize: { xs: '0.96rem', sm: '1rem' },
                  flex: '1 1 auto',
                  minWidth: 0,
                }}
              >
                Rs. {prod.price || '50'}
              </Typography>
              <CartButtons product={prod} redirectToDetails />
            </CardActions>
          </Card>
        ))}
      </Box>
    </>
  );
}
